import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { LeaderboardEntry } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

/**
 * Leaderboard configuration
 */
export interface LeaderboardConfig {
  /** Maximum entries to display */
  maxEntries: number;
  /** Entry height in pixels */
  entryHeight: number;
  /** Panel width in pixels */
  panelWidth: number;
  /** Background alpha */
  backgroundAlpha: number;
  /** Padding inside panel */
  padding: number;
  /** Corner radius */
  cornerRadius: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: LeaderboardConfig = {
  maxEntries: 10,
  entryHeight: 22,
  panelWidth: 180,
  backgroundAlpha: 0.75,
  padding: 10,
  cornerRadius: 8,
};

/**
 * Extra entry for showing local player when not in top 10
 */
const LOCAL_PLAYER_EXTRA_SLOT = 1;

/**
 * Leaderboard - PixiJS-based leaderboard display
 *
 * Features:
 * - Top 10 players list
 * - Shows rank, name, score
 * - Local player highlighted in gold/yellow
 * - Score formatting with K/M suffixes
 * - Semi-transparent background
 * - Positioned below minimap at top-right
 */
export class Leaderboard {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: LeaderboardConfig;

  /** Background graphics */
  private background: Graphics;

  /** Title text */
  private titleText: Text;

  /** Entry containers */
  private entries: Container;

  /** Entry text objects (reused for performance) */
  private entryTexts: { rank: Text; name: Text; score: Text }[] = [];

  /** Local player ID for highlighting */
  private localPlayerId: string | null = null;

  /** Whether leaderboard is visible */
  private visible: boolean = false;

  /** Current entries data */
  private currentEntries: LeaderboardEntry[] = [];

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  constructor(config: Partial<LeaderboardConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();

    // Create container
    this.container = new Container();
    this.container.label = 'leaderboard';

    // Create background
    this.background = new Graphics();
    this.container.addChild(this.background);

    // Create title
    const titleStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(14),
      fontWeight: 'bold',
      fill: 0x4ecdc4,
    });
    this.titleText = new Text({ text: 'Leaderboard', style: titleStyle });
    this.titleText.anchor.set(0.5, 0);
    this.container.addChild(this.titleText);

    // Create entries container
    this.entries = new Container();
    this.container.addChild(this.entries);

    // Pre-create entry text objects
    this.createEntryTexts();

    // Initially hidden
    this.container.visible = false;

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all elements
   */
  private applyScaling(): void {
    // Update title font size
    (this.titleText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(14);

    // Update entry fonts
    this.entryTexts.forEach(entry => {
      (entry.rank.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(12);
      (entry.name.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(12);
      (entry.score.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(11);
    });

    // Redraw
    this.redraw();
  }

  /**
   * Pre-create text objects for entries (reuse for performance)
   * Creates extra slot for local player when not in top 10
   */
  private createEntryTexts(): void {
    const { maxEntries, entryHeight, panelWidth, padding } = this.config;
    const totalSlots = maxEntries + LOCAL_PLAYER_EXTRA_SLOT;

    const nameStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(12),
      fill: 0xdddddd,
    });

    const scoreStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(11),
      fill: 0x888888,
    });

    for (let i = 0; i < totalSlots; i++) {
      const rankText = new Text({ text: '', style: nameStyle });
      rankText.position.set(padding, i * entryHeight);

      const nameText = new Text({ text: '', style: nameStyle });
      nameText.position.set(padding + 25, i * entryHeight);

      const scoreText = new Text({ text: '', style: scoreStyle });
      scoreText.anchor.set(1, 0);
      scoreText.position.set(panelWidth - padding, i * entryHeight);

      this.entries.addChild(rankText);
      this.entries.addChild(nameText);
      this.entries.addChild(scoreText);

      this.entryTexts.push({ rank: rankText, name: nameText, score: scoreText });
    }
  }

  /**
   * Set local player ID for highlighting
   */
  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
    this.redraw();
  }

  /**
   * Update leaderboard with new entries
   */
  update(entries: LeaderboardEntry[]): void {
    this.currentEntries = entries;
    this.redraw();
  }

  /**
   * Redraw the leaderboard
   */
  private redraw(): void {
    const { maxEntries, entryHeight } = this.config;
    const totalSlots = maxEntries + LOCAL_PLAYER_EXTRA_SLOT;

    // Check if we have a local player entry beyond the top 10
    const hasExtraPlayerEntry = this.currentEntries.length > maxEntries &&
      this.currentEntries[maxEntries]?.id === this.localPlayerId;

    // Update entries
    for (let i = 0; i < totalSlots; i++) {
      const entryText = this.entryTexts[i];
      if (!entryText) continue;

      const entry = this.currentEntries[i];

      if (entry) {
        const isLocal = entry.id === this.localPlayerId;
        const isExtraSlot = i >= maxEntries;
        const isLeviathan = entry.isLegendary === true;

        // Add separator styling for extra slot (local player at bottom)
        if (isExtraSlot) {
          entryText.rank.position.y = i * entryHeight + 6; // Extra spacing
          entryText.name.position.y = i * entryHeight + 6;
          entryText.score.position.y = i * entryHeight + 6;
        } else {
          entryText.rank.position.y = i * entryHeight;
          entryText.name.position.y = i * entryHeight;
          entryText.score.position.y = i * entryHeight;
        }

        // Update rank - use actual rank from entry for local player at bottom
        const displayRank = isExtraSlot ? entry.rank : i + 1;

        // LEVIATHAN gets crown emoji, local player gets gold, others get gray
        if (isLeviathan) {
          entryText.rank.text = '\u{1F451}'; // Crown emoji for LEVIATHAN
          entryText.rank.style.fill = 0xffd700; // Gold
        } else {
          entryText.rank.text = `${displayRank}.`;
          entryText.rank.style.fill = isLocal ? 0xffd700 : 0xaaaaaa;
        }

        // Update name (truncate if too long)
        let displayName = entry.name;
        if (displayName.length > 12) {
          displayName = displayName.substring(0, 11) + '...';
        }
        if (isLocal) {
          displayName += ' (You)';
        }
        entryText.name.text = displayName;

        // LEVIATHAN gets gold text, local player gets gold, others get white
        if (isLeviathan) {
          entryText.name.style.fill = 0xffd700; // Gold for LEVIATHAN
          entryText.name.style.fontWeight = 'bold';
        } else if (isLocal) {
          entryText.name.style.fill = 0xffd700;
          entryText.name.style.fontWeight = 'bold';
        } else {
          entryText.name.style.fill = 0xdddddd;
          entryText.name.style.fontWeight = 'normal';
        }

        // Update score
        entryText.score.text = this.formatScore(entry.score);

        // LEVIATHAN gets gold score, local player gets gold, others get gray
        if (isLeviathan) {
          entryText.score.style.fill = 0xffd700; // Gold for LEVIATHAN
        } else if (isLocal) {
          entryText.score.style.fill = 0xffd700;
        } else {
          entryText.score.style.fill = 0x888888;
        }

        // Show entry
        entryText.rank.visible = true;
        entryText.name.visible = true;
        entryText.score.visible = true;
      } else {
        // Hide unused entry
        entryText.rank.visible = false;
        entryText.name.visible = false;
        entryText.score.visible = false;
      }
    }

    // Redraw background
    this.drawBackground(hasExtraPlayerEntry);
  }

  /**
   * Draw background panel
   * @param hasExtraEntry Whether there's an extra entry for local player below top 10
   */
  private drawBackground(hasExtraEntry: boolean = false): void {
    const { panelWidth, entryHeight, padding, cornerRadius, backgroundAlpha, maxEntries } = this.config;

    const visibleEntries = Math.min(this.currentEntries.length, maxEntries);
    let contentHeight = visibleEntries > 0 ? visibleEntries * entryHeight : entryHeight;

    // Add extra space for local player entry below top 10
    if (hasExtraEntry) {
      contentHeight += entryHeight + 10; // Extra entry + separator space
    }

    const titleHeight = 24;
    const totalHeight = titleHeight + contentHeight + padding * 2;

    const g = this.background;
    g.clear();

    // Draw rounded rectangle background
    g.roundRect(0, 0, panelWidth, totalHeight, cornerRadius);
    g.fill({ color: 0x141423, alpha: backgroundAlpha });

    // Draw title underline
    g.moveTo(padding, titleHeight);
    g.lineTo(panelWidth - padding, titleHeight);
    g.stroke({ width: 1, color: 0x4ecdc4, alpha: 0.3 });

    // Draw separator line before local player entry if needed
    if (hasExtraEntry) {
      const separatorY = titleHeight + 6 + visibleEntries * entryHeight + 2;
      g.moveTo(padding, separatorY);
      g.lineTo(panelWidth - padding, separatorY);
      g.stroke({ width: 1, color: 0xffd700, alpha: 0.3 });
    }

    // Position title
    this.titleText.position.set(panelWidth / 2, padding);

    // Position entries
    this.entries.position.set(0, titleHeight + 6);

    // Show empty message if no entries
    if (this.currentEntries.length === 0) {
      const firstEntry = this.entryTexts[0];
      if (firstEntry) {
        firstEntry.name.text = 'No players yet';
        firstEntry.name.style.fill = 0x666666;
        firstEntry.name.position.x = panelWidth / 2 - 40;
        firstEntry.name.visible = true;
        firstEntry.rank.visible = false;
        firstEntry.score.visible = false;
      }
    }
  }

  /**
   * Format score for display
   */
  private formatScore(score: number): string {
    if (score >= 1000000) {
      return (score / 1000000).toFixed(1) + 'M';
    } else if (score >= 1000) {
      return (score / 1000).toFixed(1) + 'K';
    }
    return score.toString();
  }

  /**
   * Show the leaderboard
   */
  show(): void {
    this.visible = true;
    this.container.visible = true;
    this.redraw();
  }

  /**
   * Hide the leaderboard
   */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Handle screen resize
   */
  onResize(_width: number, _height: number): void {
    // Redraw to update layout
    this.redraw();
  }

  /**
   * Position the leaderboard below the minimap
   * @param minimapBottomY The bottom Y coordinate of the minimap
   * @param minimapCenterX The center X coordinate of the minimap
   */
  positionBelowMinimap(minimapBottomY: number, minimapCenterX: number): void {
    const { panelWidth } = this.config;
    const padding = 10;

    this.container.position.set(
      minimapCenterX - panelWidth / 2,
      minimapBottomY + padding
    );
  }

  /**
   * Position at top-right corner (standalone mode)
   */
  positionTopRight(screenWidth: number): void {
    const { panelWidth } = this.config;
    const padding = 15;

    this.container.position.set(
      screenWidth - panelWidth - padding,
      padding
    );
  }

  /**
   * Get panel width
   */
  getWidth(): number {
    return this.config.panelWidth;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.background.destroy();
    this.titleText.destroy();
    this.entryTexts.forEach(entry => {
      entry.rank.destroy();
      entry.name.destroy();
      entry.score.destroy();
    });
    this.entries.destroy({ children: true });
    this.container.destroy({ children: true });
  }
}
